import {getResourceUrl} from "../../globals";
import {ViewingService} from "../../net/Xhr";
import AT from './account-constants';
import {QC, QCOverrides, ColumnFamilies, ColumnNames, ElementFlags, SyntheticColumns, SchemaVersion} from "./dt-schema";
import {SystemClassNames} from "./system-class";
import {AttributeDef, AttributeType, AttributeFlags, AttributeContext, matchAttributeContext} from "./Attribute";
import {matchClassification, checkClassificationSystem} from './matchClassification';
import {Debounce} from "../../net/debounce";
import RC from "../resources/cats_enum.json";
import { TandemCategories } from "./TandemCategories";
import {HUD_LAYER} from "../hud/layers/HudLayerConstants";

let RESOURCES = {};

async function getResource(url) {
	return new Promise((resolve, reject) => {
		ViewingService.getItem({}, url, resolve, reject, {
			responseType: "json",
			withCredentials: false
		})
	});
}

const getResourceDebounce = new Debounce(async (name) => {
	let url = getResourceUrl(name);
	RESOURCES[name] = await getResource(url);
	return RESOURCES[name];
});


async function getResourceDebounced(name) {

	if (RESOURCES[name]) {
		return RESOURCES[name];
	}

	return getResourceDebounce.debounce(name, [name]);
}

async function getForgeUnits() {
	return getResourceDebounced("units.json");
}

async function getForgeUnitSpecs() {
	return getResourceDebounced("specs.json");
}

async function getRevitCategories() {
	return getResourceDebounced("cat_id_to_name.json");
}

async function getParameterLibrary() {
	return getResourceDebounced("parameters.json");
}

async function getFacilityTemplatesLibrary() {
	return getResourceDebounced("facilityTemplates.json");
}

async function getClassificationsLibrary() {
	return getResourceDebounced("classifications.json");
}

async function getTandemCategories() {
	return TandemCategories.classificationSystem;
}

async function getUniformatClassificationSystem() {
	const classifications = await getClassificationsLibrary();
	return classifications?.find(classification => classification.uuid === UNIFORMAT_UUID);
}
async function getMasterFormatClassificationSystem() {
	const classifications = await getClassificationsLibrary();
	return classifications?.find(classification => classification.uuid === MASTERFORMAT_UUID);
}

function isBuiltInClassificationSystem(uuid) {
	return uuid === TANDEMCATEGORIES_UUID || uuid === UNIFORMAT_UUID;
}

async function getRevitCategoryToDisciplineMapping() {

	if (CAT_TO_DISC) return CAT_TO_DISC;

	let d2c = await getRevitDisciplines();

	let res = {};

	for (let d in d2c) {
		let cats = d2c[d];

		for (let i = 0; i < cats.length; i++) {
			res[cats[i]] = d;
		}
	}

	return res;
}

function getSystemClassNames() {
	return SystemClassNames.slice();
}

/**
 * Curated collection of interesting Revit category IDs for system tracing.
 * @return {Promise<number[]>}
 */
async function getInterestingTracingCats() {
	return getResourceDebounced("cats_of_interest.json");
}

const UNIFORMAT_UUID = 'uniformat';
const MASTERFORMAT_UUID = 'masterformat';
const TANDEMCATEGORIES_UUID = TandemCategories.UUID;

const TicketPriorities = Object.freeze({
	Trivial: "Trivial",
	Low: "Low",
	Medium: "Medium",
	High: "High",
	Critical: "Critical",
});

const TicketPriority2Index = Object.freeze({
	[TicketPriorities.Trivial]: 0,
	[TicketPriorities.Low]: 1,
	[TicketPriorities.Medium]: 2,
	[TicketPriorities.High]: 3,
	[TicketPriorities.Critical]: 4,
});

const StandardAttributes = {
	[QC.RowKey]: Object.freeze(new AttributeDef(QC.RowKey, ['externalId', 'ID', AttributeType.String, null, '', '', AttributeFlags.afHidden | AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', QC.RowKey, "", null])),
	[SyntheticColumns.GUID]: Object.freeze(new AttributeDef(SyntheticColumns.GUID, ['GUID', 'Common', AttributeType.String, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '',  SyntheticColumns.GUID, "", null])),
	[SyntheticColumns.SourceFile]: Object.freeze(new AttributeDef(SyntheticColumns.SourceFile, ['Source File', 'Common', AttributeType.String, null, 'Source File', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', SyntheticColumns.SourceFile, "", null])),
	[SyntheticColumns.LocationLevelColumn]: Object.freeze(new AttributeDef(SyntheticColumns.LocationLevelColumn, ['Location Level', 'Common', AttributeType.String, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', SyntheticColumns.LocationLevelColumn, "", null])),
	[SyntheticColumns.LocationRoomColumn]: Object.freeze(new AttributeDef(SyntheticColumns.LocationRoomColumn, ['Location Room', 'Common', AttributeType.String, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', SyntheticColumns.LocationRoomColumn, "", null])),

	[QC.LmvDbId]: Object.freeze(new AttributeDef(QC.LmvDbId, ['dbId', 'ID', AttributeType.DbKey, null, '', '', AttributeFlags.afHidden | AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.LmvDbId, ColumnFamilies.Standard, null])),
	[QC.ElementFlags]: Object.freeze(new AttributeDef(QC.ElementFlags, ['Flags', 'Common', AttributeType.Integer, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.ElementFlags, ColumnFamilies.Standard, null])),
	[QC.SystemClass]: Object.freeze(new AttributeDef(QC.SystemClass, ['System Class', 'Common', AttributeType.Integer, null, '', '',  AttributeFlags.afStandard, 0, '', '', '', ColumnNames.SystemClass, ColumnFamilies.Standard, null])),
	[QC.OSystemClass]: Object.freeze(new AttributeDef(QC.OSystemClass, ['System Class Override', 'Common', AttributeType.Integer, null, '', '',  AttributeFlags.afStandard, 0, '', '', '', ColumnNames.OSystemClass, ColumnFamilies.Standard, null])),
	[QC.Name]: Object.freeze(new AttributeDef(QC.Name, ['Name', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Name, ColumnFamilies.Standard, null])),
	[QC.OName]: Object.freeze(new AttributeDef(QC.OName, ['Name Override', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.OName, ColumnFamilies.Standard, null])),
	[QC.TandemCategory]: Object.freeze(new AttributeDef(QC.TandemCategory, ['Tandem Category', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.TandemCategory, ColumnFamilies.Standard, null])),
	[QC.OTandemCategory]: Object.freeze(new AttributeDef(QC.OTandemCategory, ['Tandem Category Override', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.OTandemCategory, ColumnFamilies.Standard, null])),
	[QC.UniformatClass]: Object.freeze(new AttributeDef(QC.UniformatClass, ['Assembly Code', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.UniformatClass, ColumnFamilies.Standard, null])),
	[QC.OUniformatClass]: Object.freeze(new AttributeDef(QC.OUniformatClass, ['Assembly Code Override', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.OUniformatClass, ColumnFamilies.Standard, null])),
	[QC.Classification]: Object.freeze(new AttributeDef(QC.Classification, ['Classification', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Classification, ColumnFamilies.Standard, null])),
	[QC.OClassification]: Object.freeze(new AttributeDef(QC.OClassification, ['Classification Override', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.OClassification, ColumnFamilies.Standard, null])),
	[QC.CategoryId]: Object.freeze(new AttributeDef(QC.CategoryId, ['Category Id', 'Common', AttributeType.Integer, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.CategoryId, ColumnFamilies.Standard, null])),
	[QC.CategoryName]: Object.freeze(new AttributeDef(QC.CategoryName, ['Category Name', 'Common', AttributeType.String, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.CategoryName, ColumnFamilies.Virtual, null])),
	[QC.FamilyPath]: Object.freeze(new AttributeDef(QC.FamilyPath, ['Category/Family', 'Common', AttributeType.String, null, '', '', AttributeFlags.afHidden | AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.FamilyPath, ColumnFamilies.Standard, null])),
	[QC.Elevation]: Object.freeze(new AttributeDef(QC.Elevation, ['Elevation', 'Common', AttributeType.Double, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Elevation, ColumnFamilies.Standard, null])),
	[QC.OElevation]: Object.freeze(new AttributeDef(QC.OElevation, ['Elevation Override', 'Common', AttributeType.Double, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.OElevation, ColumnFamilies.Standard, null])),
	[QC.Height]: Object.freeze(new AttributeDef(QC.Height, ['Room Height', 'Common', AttributeType.Double, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Height, ColumnFamilies.Standard, null])),
	[QC.Tolerance]: Object.freeze(new AttributeDef(QC.Tolerance, ['Tolerance', 'Common', AttributeType.Double, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Tolerance, ColumnFamilies.Standard, null])), // DEPRECATED
	[QC.Config]: Object.freeze(new AttributeDef(QC.Config, ['Configuration', 'Common', AttributeType.BLOB, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Config, ColumnFamilies.Standard, null])),
	[QC.Settings]: Object.freeze(new AttributeDef(QC.Settings, ['Settings', 'Common', AttributeType.BLOB, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Settings, ColumnFamilies.Standard, null])),
	[QC.State]: Object.freeze(new AttributeDef(QC.State, ['State', 'Common', AttributeType.String, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.State, ColumnFamilies.Status, null])),
	[QC.Deleted]: Object.freeze(new AttributeDef(QC.Deleted, ['Deleted', 'Common', AttributeType.Boolean, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Deleted, ColumnFamilies.Status, null])),
	[QC.ODeleted]: Object.freeze(new AttributeDef(QC.ODeleted, ['Deleted Override', 'Common', AttributeType.Boolean, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.ODeleted, ColumnFamilies.Status, null])),

	[QC.Location]: Object.freeze(new AttributeDef(QC.Location, ['Location', 'Common', AttributeType.ExDbKeyList, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Location, ColumnFamilies.Xrefs, null])),
	[QC.OLocation]: Object.freeze(new AttributeDef(QC.OLocation, ['Location Override', 'Common', AttributeType.ExDbKeyList, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.OLocation, ColumnFamilies.Xrefs, null])),
	[QC.Level]: Object.freeze(new AttributeDef(QC.Level, ['Level', 'Common', AttributeType.DbKey, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Level, ColumnFamilies.Refs, null])),
	[QC.OLevel]: Object.freeze(new AttributeDef(QC.OLevel, ['Level Override', 'Common', AttributeType.DbKey, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.OLevel, ColumnFamilies.Refs, null])),
	[QC.TopLevel]: Object.freeze(new AttributeDef(QC.TopLevel, ['Top Level', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.TopLevel, ColumnFamilies.Refs, null])),
	[QC.Rooms]: Object.freeze(new AttributeDef(QC.Rooms, ['Rooms', 'Common', AttributeType.DbKeyList, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Rooms, ColumnFamilies.Refs, null])),
	[QC.ORooms]: Object.freeze(new AttributeDef(QC.ORooms, ['Rooms Override', 'Common', AttributeType.DbKeyList, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.ORooms, ColumnFamilies.Refs, null])),
	[QC.XRooms]: Object.freeze(new AttributeDef(QC.XRooms, ['Rooms External', 'Common', AttributeType.ExDbKeyList, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Rooms, ColumnFamilies.Xrefs, null])),
	[QC.OXRooms]: Object.freeze(new AttributeDef(QC.OXRooms, ['Rooms External Override', 'Common', AttributeType.ExDbKeyList, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.ORooms, ColumnFamilies.Xrefs, null])),
	[QC.Parent]: Object.freeze(new AttributeDef(QC.Parent, ['Parent', 'Common', AttributeType.DbKey, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Parent, ColumnFamilies.Refs, null])),
	[QC.XParent]: Object.freeze(new AttributeDef(QC.XParent, ['Parent External', 'Common', AttributeType.ExDbKeyList, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Parent, ColumnFamilies.Xrefs, null])),
	[QC.XHostOfAsset]: Object.freeze(new AttributeDef(QC.XHostOfAsset, ['Host of Asset', 'Common', AttributeType.DbKey, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.HostOfAsset, ColumnFamilies.Xrefs, null])),
	[QC.BimElmOfAsset]: Object.freeze(new AttributeDef(QC.BimElmOfAsset, ['BIM Element of Asset', 'Common', AttributeType.DbKey, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.BimElmOfAsset, ColumnFamilies.Xrefs, null])),
	[QC.AssetSplitFromBimElm]: Object.freeze(new AttributeDef(QC.AssetSplitFromBimElm, ['Asset Split from BIM Element', 'Common', AttributeType.DbKey, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.AssetSplitFromBimElm, ColumnFamilies.Xrefs, null])),
	[QC.FamilyType]: Object.freeze(new AttributeDef(QC.FamilyType, ['Family Type', 'Common', AttributeType.DbKey, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.FamilyType, ColumnFamilies.Refs, null])),
	[QC.SubFamily]: Object.freeze(new AttributeDef(QC.SubFamily, ['Sub Family', 'Common', AttributeType.DbKey, null, '', '', AttributeFlags.afReadOnly | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.SubFamily, ColumnFamilies.Refs, null])),
	[QC.XElements]: Object.freeze(new AttributeDef(QC.XElements, ['Elements External', 'Common', AttributeType.ExDbKeyList, null, '', '', AttributeFlags.afHidden | AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Elements, ColumnFamilies.Xrefs, null])),

	[QC.LmvFragment]: Object.freeze(new AttributeDef(QC.LmvFragment, ['Fragment', 'Common', AttributeType.BLOB, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Fragment, ColumnFamilies.LMV, null])),
	[QC.LmvBoundingBox]: Object.freeze(new AttributeDef(QC.LmvBoundingBox, ['Bounding Box', 'Common', AttributeType.BLOB, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.BoundingBox, ColumnFamilies.LMV, null])),
	[QC.TransformHint]: Object.freeze(new AttributeDef(QC.TransformHint, ['Transform Hint', 'Common', AttributeType.BLOB, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.TransformHint, ColumnFamilies.LMV, null])),
	[QC.Centerline]: Object.freeze(new AttributeDef(QC.Centerline, ['Centerline', 'Common', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Centerline, ColumnFamilies.LMV, null])),
	[QC.CenterlinePlane]: Object.freeze(new AttributeDef(QC.CenterlinePlane, ['Centerline Plane', 'Common', AttributeType.Position, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.CenterlinePlane, ColumnFamilies.LMV, null])),

	[QC.Priority]: Object.freeze(new AttributeDef(QC.Priority, ['Priority', 'Tickets', AttributeType.String, null, '', '', AttributeFlags.afStandard, 0, '', '', '', ColumnNames.Priority, ColumnFamilies.Standard, null, { list: Object.values(TicketPriorities) }])),
	[QC.OpenDate]: Object.freeze(new AttributeDef(QC.OpenDate, ['Open Date', 'Tickets', AttributeType.DateTime, null, '', '', AttributeFlags.afStandard, 0, '', 'date', '', ColumnNames.OpenDate, ColumnFamilies.Standard, null])),
	[QC.CloseDate]: Object.freeze(new AttributeDef(QC.CloseDate, ['Close Date', 'Tickets', AttributeType.DateTime, null, '', '', AttributeFlags.afStandard, 0, '', 'date', '', ColumnNames.CloseDate, ColumnFamilies.Standard, null])),
	[QC.IsAsset]: Object.freeze(new AttributeDef(QC.IsAsset, ["Is Asset", "Common", AttributeType.Boolean, null, '', '', AttributeFlags.afHidden, 0, '', '', '', ColumnNames.IsAsset, ColumnFamilies.Standard, null]))
};


const FamilyAttributes = {
	// Special Attr definition for systems column. Includes ":" to match StandardAttributes format
	[ColumnFamilies.Systems + ':']: Object.freeze(new AttributeDef(ColumnFamilies.Systems + ':', ['Systems', 'Common', null, null, '', '', AttributeFlags.afReadOnly, 0, ''])),
};

//Revit properties with these categories are excluded from the data grid UI.
//Some of these are normally not visible in Revit while the others are considered not relevant.
const HiddenRevitCategories = ["__category__", "__categoryId__", "Graphics", "Phasing", "__node_flags__"];

const ModelImportState = {
	// ModelCreated model row was created
	Created: "c",
	// ModelReady model is ready for viewing and editing
	Ready: "r",
	// ModelImportPending model is queued for import
	ImportPending: "q",
	// ModelImporting import process is running
	Importing: "i",
	// ModelFailed something went wrong during the pipeline steps
	Failed: "f",
	// ModelTranslating processing by Forge Derivative Service
	Translating: "t",
	// ModelPostProcessing operations that do bulk updates to model data
	PostProcessing: "p",
	// ModelDeleting - model was queued for deletion
	Deleting: "d",
};

const SystemState = {
	// System created and mapped with up-to-date element properties
	Ready: "r",
	// Possible element(s) added/removed from system, remapping may be needed
	Dirty: "d"
}

// TODO: normalize accesslevel definitions across layers
const DtAccessLevel = Object.freeze({
	NONE: "None",
	READ: "Read",
	READWRITE: "ReadWrite",
	MANAGE: "Manage",
	OWNER: "Owner",
});

const DT_URN_PREFIX = "urn:adsk.dt";
const DT_MODEL_URN_PREFIX = "urn:adsk.dtm";
const DT_TWIN_URN_PREFIX = "urn:adsk.dtt";
const DT_USER_URN_PREFIX = "urn:adsk.dtu";
const DT_GROUP_URN_PREFIX = "urn:adsk.dtg";
const DT_DOCUMENT_URN_PREFIX = "urn:adsk.dtd";

const ChangeTypes = Object.freeze({
	BulkImport: 'bulk_import',
	BulkUpdate: 'bulk_update',
	BulkFail: 'bulk_fail',
	Mutate: 'mutate',
	ApplyPset: 'apply_pset',
	DeletePset: 'delete_pset',
	UpdateClassification: 'update_classification',
	StateChange: 'state_change',
	UpdateMetrics: 'metrics_update',
	ApplyTemplate: 'apply_template',
	RemoveTemplate: 'remove_template',
	DeleteStreamData: 'delete_stream_data',
	UpdateSystems: 'update_systems',
	UpdateSystemConnections: 'update_system_connections',
	UpdateTwinSettings: 'update_twin_settings',
	MoveTwin: "move_twin",
	UpdateGroupTwins: "update_group_twins",
	UpdateGroupACL: "acl_update_group",
	BulkUpdateGroupACL: "acl_bulk_update_group",
	DeleteGroupUserACL: "acl_delete_group_user",
	BulkDeleteGroupUserACL: "acl_bulk_delete_group_user",
	UpdateTwinACL: "acl_update_twin",
	BulkUpdateTwinACL: "acl_bulk_update_twin",
	DeleteTwinUserACL: "acl_delete_twin_user",
	BulkDeleteTwinUserACL: "acl_bulk_delete_twin_user",
	AddDocument: 'add_document',
	DeleteDocument: 'delete_document',
	UpdateDocument: 'update_document',
	Maintenance: 'db_maintenance',
	UpdateIot: 'update_iot',
	UpdateView: 'update_view',
	StreamAlert: 'stream_alert',
	StreamConnectivity: 'stream_connectivity',
	TransitionReplay: 'transition_replay',
	UpdateSimulation: 'update_simulation',
});


// Adding constants here needs to be reflected in the client for translations
// Also consider if the action is non-revertible and add it to `NON_REVERTABLE_MUTATIONS` in dt-client
const ChangeDesc = Object.freeze({
	DeleteElement: 'element-delete',
	ExcelImport: 'Excel import',

	CreateLevels: 'Create levels',
	CreateSpace: 'create_space',
	UpdateSpace: 'update_space',
	UpdateRoomRelations: 'Update rooms relationship',
	UpdateLocationRelations: 'Update locations relationship',

	CreateStreams: 'Create streams',
	DeleteStreams: 'stream-delete',
	UpdateStreams: 'Update streams',
	UpdateStreamsConfig: 'stream-config-update',
	UpdateStreamsDPUsage: 'stream-data-point-usage-update',
	UpdateStreamsRelationship: 'Update streams relationship',
	UpdateStreamsThresholds: 'Update streams thresholds',
	UpdateStreamsStateSettings: 'Update streams state settings',
	UpdateStreamOfflineTimeouts: 'Update streams offline timeouts',
	UpdateStreamsSrcMappingOverrides: 'Update streams source mapping overrides',
	UpdateStreamsCalculationSettings: 'Update streams calculation settings',
	StreamCalculationAutoDisabled: 'Stream calculation auto-disabled due to consecutive timeouts',

	CreateSystem: 'Create system',
	DeleteSystem: 'system-delete',
	UpdateSystem: 'Update system',
	UpdateSystemClass: 'Update systems classification',
	UpdateSystemConnections: 'Update system connections',
	UpdateSystemState: 'Update system state',

	CreateSubsystems: 'Create subsystems',
	UpdateSubsystems: 'Update subsystems',

	CreateTicket: 'Create ticket',

	UpdateHosts: 'Update hosts',
	MakeAssets: 'Make assets',
	RefreshStreamConfigs: 'Refresh streams configs due to update of facility template',
	RefreshStreamConfigsClassificationChange: 'Refresh streams configs due to stream classification change',

	CreateLBS: 'createLBS', // matches server-side string (and style)

	AssetImportCreate: 'Asset import create elements',
	AssetImportUpdate: 'Asset import update elements',
});

const MutationActions = Object.freeze({
	Upsert: 'i',
	Delete: 'd',
	DeleteRow: 'a'
});

const SystemFilterNames = Object.freeze({
	SystemClasses: "systemClasses",
	MembershipFilter: "membershipFilter",
});

const StreamStates = Object.freeze({
	NoData: "no data",
	Normal: "normal",
	Warning: "warning",
	Alert: "alert",
	Offline: "offline",
});
// Time after which we consider streams offline in seconds
const StreamOfflineTimeouts = Object.freeze({
	Default: 900, // 15 minutes
	Never: 0
});

// Time after which stream alert will be created
const StreamAlertEvaluationPeriod = 600;

// order by key index
const StreamStatePriority = Object.values(StreamStates)
	.reduce((current, key, i) => ({ ...current, [key]: i }), {});

// Map stream AlertState from server notifications & history to StreamStates
const StreamAlertState2State = Object.freeze([
	StreamStates.Normal,
	StreamStates.Warning,
	StreamStates.Alert
]);

const StreamMetricStates = Object.freeze({
	Available: "available",
	Active: "active",
	Scheduled: "scheduled",
});

const StreamStateBitmask = Object.freeze({
	NORMAL:    0b00000,
	WARN:      0b00001,
	ALERT:     0b00010,
	AVAILABLE: 0b00100,
	ACTIVE:    0b01000,
	SCHEDULED: 0b10000,
});

const THRESHOLD_ALERTS_MASK = StreamStateBitmask.WARN | StreamStateBitmask.ALERT;
const NAMED_STATES_MASK = StreamStateBitmask.AVAILABLE | StreamStateBitmask.ACTIVE | StreamStateBitmask.SCHEDULED;

/**
 * 1, 0, 0, 0,														(4 Logical element flags)
 * 77, 79, 78, 84, 82, 69, 65, 76, 80, 79, 84, 72, 79, 76, 69, 83,	(16 Hard coded MEPSystem UUIDv4: 4d4f4e54-5245-414c-504f-54484f4c4553)
 * 0, 0, 0, 0,														(4 Incrementing System ID)
 */
const SystemElementFirstKey = "AQAAAE1PTlRSRUFMUE9USE9MRVMAAAAA";

// Same list as CENTERLINE_CATEGORY_IDS_SET on the server
const CenterlineCatIDs = Object.freeze({
	[RC.DuctCurvesCenterLine]: true,
	[RC.FlexDuctCurvesCenterLine]: true,
	[RC.PipeCurvesCenterLine]: true,
	[RC.FlexPipeCurvesCenterLine]: true,
	[RC.DuctFittingCenterLine]: true,
	[RC.PipeFittingCenterLine]: true,
	[RC.CableTrayCenterLine]: true,
	[RC.ConduitCenterLine]: true,
	[RC.CableTrayFittingCenterLine]: true,
	[RC.ConduitFittingCenterLine]: true,
	[RC.FabricationDuctworkCenterLine]: true,
	[RC.FabricationPipeworkCenterLine]: true,
	[RC.FabricationContainmentCenterLine]: true,
});

const Timeline = Object.freeze({
	// Time intervals for the telemetry window.
	Intervals: {
		LAST_3_DAYS: 'LAST_3_DAYS',
		LAST_7_DAYS: 'LAST_7_DAYS',
		CUSTOM: 'CUSTOM',
	},
	// Symbolic representation of the current time.
	Now: 'dynamic_now',
});

const ClassificationAttribute2FilterKey = Object.freeze({
	[QC.TandemCategory]: 'tandemCategory',
	[QC.UniformatClass]: 'dtClass',
	[QC.Classification]: 'userClass',
});

const TC = Object.freeze({
	LocationPrefix: 'L.',
	Location: 'L',
	Levels: 'L.Lv',
	Rooms: 'L.Rm',
	Sensors: 'Q.Se',
	Ticket: 'I.Ti',
	Metrics: 'I.Me',
})

const TandemRegions = Object.freeze({
	US: 'us',
	EU: 'eu',
	AUS: 'aus',
});

const SystemAttributes = Object.freeze({
	[QC.Name]: true,
	[QC.SystemClass]: true,
	[QC.TandemCategory]: true,
	[QC.Classification]: true,
	[QC.UniformatClass]: true, // Defined on creation, read-only
})

export let DtConstants = {
	AT,
	getForgeUnits,
	getForgeUnitSpecs,
	getInterestingTracingCats,
	getRevitCategories,
	getParameterLibrary,
	getFacilityTemplatesLibrary,
	getClassificationsLibrary,
	getTandemCategories,
	getUniformatClassificationSystem,
	getMasterFormatClassificationSystem,
	getRevitCategoryToDisciplineMapping,
	getSystemClassNames,
	matchClassification,
	checkClassificationSystem,
	isBuiltInClassificationSystem,
	matchAttributeContext,
	QC,
	QCOverrides,
	ColumnFamilies,
	ColumnNames,
	ElementFlags,
	StandardAttributes,
	FamilyAttributes,
	SyntheticColumns,
	HiddenRevitCategories,
	ModelImportState,
	MutationActions,
	UNIFORMAT_UUID,
	MASTERFORMAT_UUID,
	TANDEMCATEGORIES_UUID,
	DtAccessLevel,
	DT_MODEL_URN_PREFIX,
	DT_TWIN_URN_PREFIX,
	DT_USER_URN_PREFIX,
	DT_GROUP_URN_PREFIX,
	DT_DOCUMENT_URN_PREFIX,
	DT_URN_PREFIX,
	ChangeTypes,
	ChangeDesc,
	RC,
	TC,
	AttributeFlags,
	AttributeType,
	AttributeContext,
	SystemFilterNames,
	SystemElementFirstKey,
	SystemState,
	CenterlineCatIDs,
	StreamStates,
	StreamMetricStates,
	StreamAlertState2State,
	StreamStateBitmask,
	THRESHOLD_ALERTS_MASK,
	NAMED_STATES_MASK,
	StreamAlertEvaluationPeriod,
	StreamOfflineTimeouts,
	StreamStatePriority,
	SystemAttributes,
	Timeline,
	ClassificationAttribute2FilterKey,
	HUD_LAYER,
	TandemRegions,
	SchemaVersion,
	TicketPriorities,
	TicketPriority2Index,
};
